/**
 * In-app viewer for scene attachments (PDF / Markdown / sheet preview / image).
 * Does not use Google/Office iframes — previews are produced server-side
 * (LibreOffice PDF for Office docs) or rendered locally (Markdown / tables).
 */
import React, { useEffect, useState } from 'react';

/** Default TeachIQ attachment API prefix. Platform callers can override. */
const DEFAULT_ATTACHMENTS_BASE = '/scenario-os/coach/attachments';

function SimpleMarkdown({ text }) {
  // Lightweight: headings + paragraphs; matches ContentPanelSegment spirit.
  const lines = (text || '').split('\n');
  const blocks = [];
  let para = [];
  const flush = () => {
    if (para.length) {
      blocks.push({ type: 'p', text: para.join(' ') });
      para = [];
    }
  };
  lines.forEach((line) => {
    const h = /^(#{1,3})\s+(.+)/.exec(line);
    if (h) {
      flush();
      blocks.push({ type: `h${h[1].length}`, text: h[2] });
      return;
    }
    if (!line.trim()) {
      flush();
      return;
    }
    para.push(line.trim());
  });
  flush();
  return (
    <div style={{ lineHeight: 1.55, color: 'var(--text-primary, #111827)' }}>
      {blocks.map((b, i) => {
        if (b.type === 'h1') return <h2 key={i} style={{ fontSize: '1.25rem', margin: '0.75rem 0 0.35rem' }}>{b.text}</h2>;
        if (b.type === 'h2') return <h3 key={i} style={{ fontSize: '1.1rem', margin: '0.7rem 0 0.3rem' }}>{b.text}</h3>;
        if (b.type === 'h3') return <h4 key={i} style={{ fontSize: '1rem', margin: '0.6rem 0 0.25rem' }}>{b.text}</h4>;
        return <p key={i} style={{ margin: '0 0 0.65rem', fontSize: '0.92rem' }}>{b.text}</p>;
      })}
    </div>
  );
}

function SheetTable({ sheetPreview }) {
  const sheets = sheetPreview?.sheets || [];
  const [idx, setIdx] = useState(0);
  const sheet = sheets[idx] || { rows: [] };
  if (!sheets.length) {
    return <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>No sheet preview available.</div>;
  }
  return (
    <div>
      {sheets.length > 1 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          {sheets.map((s, i) => (
            <button
              key={s.name || i}
              type="button"
              onClick={() => setIdx(i)}
              style={{
                padding: '4px 10px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: i === idx ? '#e0e7ff' : '#fff',
                cursor: 'pointer',
                fontSize: '0.8rem',
              }}
            >
              {s.name || `Sheet ${i + 1}`}
            </button>
          ))}
        </div>
      )}
      <div style={{ overflow: 'auto', maxHeight: '60vh', border: '1px solid #e5e7eb', borderRadius: 6 }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '0.78rem', width: '100%' }}>
          <tbody>
            {(sheet.rows || []).map((row, ri) => (
              <tr key={ri} style={{ background: ri === 0 ? '#f3f4f6' : 'transparent' }}>
                {(row || []).map((cell, ci) => (
                  <td
                    key={ci}
                    style={{
                      border: '1px solid #e5e7eb',
                      padding: '4px 8px',
                      whiteSpace: 'nowrap',
                      fontWeight: ri === 0 ? 600 : 400,
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function DocumentViewer({
  sceneId,
  attachment,
  apiBase = '',
  /** Override for Feenix platform routes, e.g. `/api/v1/documents`. */
  attachmentsBase = DEFAULT_ATTACHMENTS_BASE,
  onClose,
}) {
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const kind = attachment?.viewer_kind || 'download';
  const base = `${apiBase}${attachmentsBase}`;
  const viewUrl = `${base}/view/${sceneId}/${attachment.attachment_id}`;
  const downloadUrl = `${base}/download/${sceneId}/${attachment.attachment_id}`;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        if (kind === 'pdf' || kind === 'image') {
          setPayload({ viewer_kind: kind, url: viewUrl });
          return;
        }
        const res = await fetch(viewUrl, { credentials: 'include' });
        if (!res.ok) {
          const detail = await res.json().catch(() => ({}));
          throw new Error(detail?.detail?.message || detail?.detail || `HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) setPayload(data);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [sceneId, attachment?.attachment_id, kind, viewUrl]);

  return (
    <div
      role="dialog"
      aria-label="Document viewer"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 10,
          width: 'min(960px, 96vw)',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <div style={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {attachment?.title || attachment?.filename || 'Document'}
          </div>
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '0.82rem', color: '#2563eb' }}
          >
            Download original
          </a>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: '1px solid #d1d5db',
              background: '#fff',
              borderRadius: 6,
              padding: '4px 10px',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
        <div style={{ padding: 14, overflow: 'auto', flex: 1, minHeight: 240 }}>
          {loading && <div style={{ color: '#6b7280' }}>Loading preview…</div>}
          {error && (
            <div style={{ color: '#b91c1c', fontSize: '0.9rem' }}>
              Preview unavailable ({error}). Use Download original.
            </div>
          )}
          {!loading && !error && payload?.viewer_kind === 'pdf' && (
            <iframe
              title="PDF preview"
              src={payload.url}
              style={{ width: '100%', height: '70vh', border: '1px solid #e5e7eb', borderRadius: 6 }}
            />
          )}
          {!loading && !error && payload?.viewer_kind === 'image' && (
            <img
              src={payload.url}
              alt={attachment?.title || 'Attachment'}
              style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }}
            />
          )}
          {!loading && !error && payload?.viewer_kind === 'markdown' && (
            <SimpleMarkdown text={payload.markdown_text || ''} />
          )}
          {!loading && !error && payload?.viewer_kind === 'sheet_preview' && (
            <SheetTable sheetPreview={payload.sheet_preview} />
          )}
        </div>
      </div>
    </div>
  );
}
